import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import "../styles/MainPage.css";
import Carrossel from "../components/Carrossel/Carrossel";
import { useTypewriter, Cursor } from "react-simple-typewriter";
import { FaGithubSquare, FaLinkedin } from "react-icons/fa";
import { GoSun } from "react-icons/go";
import { RxDoubleArrowDown } from "react-icons/rx";
import { GiHamburgerMenu } from "react-icons/gi";
import { MdClose } from "react-icons/md";
import Card from "../components/Card/Card";
import ExperienceField from "../components/ExperienceField/ExperienceField";
import { BsFillSendFill } from "react-icons/bs";
import { useTranslation } from 'react-i18next';

const MainPage = () => {
    const navigate = useNavigate();
    const [isLightMode, setIsLightMode] = useState(() => {
        const saved = localStorage.getItem('lightMode');
        return saved ? JSON.parse(saved) : false;
    });

    const [showScrollSign, setShowScrollSign] = useState(true);
    const [showFooterSign, setShowFooterSign] = useState(false);
    const [isCardsVisible, setIsCardsVisible] = useState(false);
    const [isExperienceVisible, setIsExperienceVisible] = useState(false);
    const [isAboutMeVisible, setIsAboutMeVisible] = useState(false);
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const { t, i18n } = useTranslation();
    const normalizeLang = (lng) => (lng ? lng.split('-')[0] : 'en');
    const [language, setLanguage] = useState(normalizeLang(i18n.language));
    const cardsAreaRef = React.useRef(null);
    const experienceAreaRef = React.useRef(null);
    const aboutMeImageRef = React.useRef(null);
    const [jobTitleWords, setJobTitleWords] = useState(["Software Engineer", "Software Developer", "Full-Stack Engineer"]);
    const [projectsText, setProjectsText] = useState(["Projects"]);
    const [experienceText, setExperienceText] = useState(["Experience"]);
    const [aboutMeText, setAboutMeText] = useState(["Hey There"]);

    useEffect(() => {
        localStorage.setItem('lightMode', JSON.stringify(isLightMode));
        
        // Selecionamos as meta tags
        const metaThemeColor = document.querySelector("meta[name=theme-color]");
        const metaAppleStatusBar = document.querySelector("meta[name=apple-mobile-web-app-status-bar-style]");
        
        if (isLightMode) {
            document.documentElement.classList.add('light-mode');
            // Usamos setAttribute para não reatribuir a variável, evitando o erro de const
            if (metaThemeColor) metaThemeColor.setAttribute("content", "#F7F3EB");
            if (metaAppleStatusBar) metaAppleStatusBar.setAttribute("content", "default");
        } else {
            document.documentElement.classList.remove('light-mode');
            if (metaThemeColor) metaThemeColor.setAttribute("content", "#00001C");
            if (metaAppleStatusBar) metaAppleStatusBar.setAttribute("content", "black-translucent");
        }
    }, [isLightMode]);

    useEffect(() => {
        setLanguage(normalizeLang(i18n.language));
    }, [i18n.language]);

    useEffect(() => {
        const titles = t("job-title", { returnObjects: true });
        if (Array.isArray(titles)) {
            setJobTitleWords(titles);
        }
        setProjectsText([t('text-projects')]);
        setExperienceText([t('text-experience')]);
        setAboutMeText([t('text-aboutme')]);
    }, [i18n.language, t]);

    const [textHome] = useTypewriter({
        words: jobTitleWords.length > 0 ? jobTitleWords : ["Software Engineer", "Software Developer", "Full-Stack Engineer"],
        loop: true,
        typeSpeed: 120,
        deleteSpeed: 80,
        delaySpeed: 1500,
    });
    const [textProject] = useTypewriter({
        words: projectsText,
        loop: true,
        typeSpeed: 120,
        deleteSpeed: 80,
        delaySpeed: 1200,
    });
    const [textExperience] = useTypewriter({
        words: experienceText,
        loop: true,
        typeSpeed: 120,
        deleteSpeed: 80,
        delaySpeed: 1200,
    });
    const [textAboutMe] = useTypewriter({
        words: aboutMeText,
        loop: true,
        typeSpeed: 120,
        deleteSpeed: 80,
        delaySpeed: 1200,
    });

    useEffect(() => {
        const handleScroll = () => {
            const shouldShow = window.scrollY < 100;
            setShowScrollSign(shouldShow);
            const nearBottom = (window.innerHeight + window.scrollY) >= (document.documentElement.scrollHeight - 10);
            setShowFooterSign(nearBottom);
        };

        handleScroll();
        window.addEventListener("scroll", handleScroll, { passive: true });
        window.addEventListener("resize", handleScroll);
        return () => {
            window.removeEventListener("scroll", handleScroll);
            window.removeEventListener("resize", handleScroll);
        };
    }, []);

    useEffect(() => {
        const currentCardsArea = cardsAreaRef.current;
        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        setIsCardsVisible(true);
                    } else {
                        setIsCardsVisible(false);
                    }
                });
            },
            { threshold: 0.1 }
        );

        if (currentCardsArea) {
            observer.observe(currentCardsArea);
        }

        return () => {
            if (currentCardsArea) {
                observer.unobserve(currentCardsArea);
            }
        };
    }, []);

    useEffect(() => {
        const currentExperienceArea = experienceAreaRef.current;
        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        setIsExperienceVisible(true);
                    } else {
                        setIsExperienceVisible(false);
                    }
                });
            },
            { threshold: 0.1 }
        );

        if (currentExperienceArea) {
            observer.observe(currentExperienceArea);
        }

        return () => {
            if (currentExperienceArea) {
                observer.unobserve(currentExperienceArea);
            }
        };
    }, []);

    useEffect(() => {
        const currentAboutMeImage = aboutMeImageRef.current;
        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        setIsAboutMeVisible(true);
                    } else {
                        setIsAboutMeVisible(false);
                    }
                });
            },
            { threshold: 0.1 }
        );

        if (currentAboutMeImage) {
            observer.observe(currentAboutMeImage);
        }

        return () => {
            if (currentAboutMeImage) {
                observer.unobserve(currentAboutMeImage);
            }
        };
    }, []);


    const toggleTheme = () => {
        setIsLightMode(!isLightMode);
    };

    const toggleLanguage = () => {
        const current = normalizeLang(language);
        const newLng = current === 'pt' ? 'en' : 'pt';
        setLanguage(newLng);
        i18n.changeLanguage(newLng);
    };

    const projectsCards = t('projects', {returnObjects: true});
    const experienceFields = t('experience-fields', {returnObjects: true});
    const aboutMeParagraphs = t("aboutme-paragraphs", {returnObjects: true});

    return(
        <div className={`${isLightMode ? "MainPage light-mode" : "MainPage"} ${language === 'pt' ? 'lang-pt' : 'lang-en'}`}>
            <div className="home">
                <div className="theme-area">
                    <button onClick={toggleLanguage} aria-label={language === 'pt' ? 'Switch to English' : 'Mudar para Português'} style={{fontSize: "40px"}}>
                        <span className="icons" role="img" aria-hidden="true">{language === 'pt' ? '🇺🇸' : '🇧🇷'}</span>
                    </button>
                    <button onClick={toggleTheme} aria-label="Alterar tema">
                        <GoSun className="icons"/>
                    </button>
                </div>

                <div className="content-area">
                    <h2>Augusto Ildefonso</h2>
                    <h1>
                        <span>{textHome}</span>
                        <Cursor 
                            cursorStyle='_' 
                            cursorColor='inherit'
                        />
                    </h1>
                    <p>{t('impact-phrase')}</p>
                    <Carrossel/>

                    <div className={`scroll-sign ${!showScrollSign ? 'hidden' : ''}`}>
                        <p>Scroll</p>
                        <RxDoubleArrowDown className="icons"/>
                    </div>
                </div>

                <div className="social-area">
                    <a href="https://github.com/Augusto-Ildefonso" rel="noreferrer">
                        <FaGithubSquare className="icons" style={{marginBottom: "0px"}}/>
                    </a>
                    <a href="https://www.linkedin.com/in/augustofernandesildefonso/" target="_blank" rel="noreferrer">
                        <FaLinkedin className="icons"/>
                    </a>
                </div>

                <button className={`mobile-menu-button ${isMobileMenuOpen ? 'hidden' : ''}`} onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} aria-label="Open menu">
                    {isMobileMenuOpen ? <MdClose className="icons" /> : <GiHamburgerMenu className="icons" />}
                </button>

                {isMobileMenuOpen && (
                    <div className="mobile-menu">
                        <div className="mobile-menu-content">
                            <button className="close-button" onClick={() => setIsMobileMenuOpen(false)}>
                                <MdClose className="icons" />
                            </button>
                            <div className="menu-section">
                                <a href="https://github.com/Augusto-Ildefonso" className="menu-item" rel="noreferrer">
                                    <FaGithubSquare className="icons" />
                                    GitHub
                                </a>
                                <a href="https://www.linkedin.com/in/augustofernandesildefonso/" target="_blank" className="menu-item" rel="noreferrer">
                                    <FaLinkedin className="icons" />
                                    LinkedIn
                                </a>
                            </div>
                            <div className="menu-section">
                                <button onClick={toggleLanguage} className="menu-item" aria-label="Toggle language">
                                    <span className="icons" style={{fontSize: "24px"}}>{language === 'pt' ? '🇺🇸' : '🇧🇷'}</span>
                                    {language === 'pt' ? 'English' : 'Português'}
                                </button>
                            </div>
                            <div className="menu-section">
                                <button onClick={toggleTheme} className="menu-item" aria-label="Toggle theme">
                                    <GoSun className="icons" />
                                    {isLightMode ? 'Dark Mode' : 'Light Mode'}
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
            
            <div className="projects-area">
                    <span>
                        {textProject}
                        <Cursor 
                            cursorStyle='_' 
                            cursorColor='inherit'
                        />    
                    </span>
                    <div ref={cardsAreaRef} className={`cards-area ${isCardsVisible ? 'visible' : ''}`}>
                        {projectsCards.map((proj, index) => (
                            <Card 
                            key={index}
                            nomeProjeto={proj.nome}
                            descricao={proj.description}
                            numTag={proj.numTags}
                            tagTexts={proj.tagTexts}
                            tagColors={proj.tagColor}
                            link={proj.link}
                            linkmessage={proj.linkMessage}
                            />
                        ))}
                    </div>
                
            </div>

            <div ref={experienceAreaRef} className="experience-area">
                <span>
                    {textExperience}
                    <Cursor 
                        cursorStyle='_' 
                        cursorColor='inherit'
                    />    
                </span>
                {experienceFields.map((field, index) => (
                    <ExperienceField 
                    key={index}
                    className={isExperienceVisible ? 'visible' : ''} 
                    src={field.src}
                    alt={field.alt}
                    date={field.date}
                    title={field.title}
                    items={field.items}
                    />
                ))}
            </div>

            <div className="aboutme-area">
                <img
                    ref={aboutMeImageRef}
                    className={isAboutMeVisible ? 'aboutme-image visible' : 'aboutme-image'}
                    src="/images/Me.png"
                    alt="Augusto Ildefonso"
                />
                <div className="aboutme-text-area">
                    <span>
                        {textAboutMe}
                        <Cursor 
                            cursorStyle='_' 
                            cursorColor='inherit'
                        />    
                    </span>
                    {aboutMeParagraphs.map((parar, index) => (
                        <p style={{marginBottom: "10px"}} key={index} dangerouslySetInnerHTML={{ __html: parar }} />
                    ))}

                    <button className="button-message" onClick={() => navigate('/message')}>
                        <BsFillSendFill style={{width: "42px", height: "42px"}}/>
                        {t('message-button')}
                    </button>
                </div>
                
            </div>

            <div className={`footer-sign ${!showFooterSign ? 'hidden' : ''}`} aria-live="polite">
                <p>© 2026 Augusto Ildefonso. {t('footer')}.</p>
            </div>
        </div>
    );
}

export default MainPage;